import { z } from 'zod'
import { planningTimeSchema, supplierPlanSchema } from './supplier-plan'

const text = z.string().trim().max(5000)
const short = z.string().trim().max(300)
const contact = z.object({ name: short, contact: z.string().trim().max(500) })
const trial = z.object({
  date: z.iso.datetime({ local: true, precision: -1 }).or(z.literal('')),
  amount: z.number().int().min(0).max(100_000_000),
})
export const scheduleCategories = ['public', 'organization'] as const
export type ScheduleCategory = (typeof scheduleCategories)[number]
const ceremonyDecision = z.enum(['undecided', 'want', 'not-want'])
const exitAndAisleScenario = z.object({
  preparedBy: short,
  notes: text,
})
const congratulationsScenario = z.object({
  location: short,
  next: text,
})
const ceremonyHelperRole = z.enum(['coordinator', 'venue-staff', 'someone-else'])
const seatingScenario = z.object({
  usherRole: ceremonyHelperRole.nullable(),
  usher: short,
  notes: text,
})
const processionalScenario = z.object({
  entries: z
    .array(
      z.object({
        id: z.uuid(),
        person: short,
      }),
    )
    .max(50),
  cueByRole: ceremonyHelperRole.nullable(),
  cueBy: short,
  notes: text,
})
const ritualItem = z.object({
  id: z.uuid(),
  kind: z.enum(['vows', 'reading', 'speech', 'ritual', 'custom']),
  title: short,
  people: short,
})
const vowsAndRitualsScenario = z.object({
  items: z.array(ritualItem).max(50),
  notes: text,
})
const ceremonyScenario = z.object({
  seating: seatingScenario.nullable(),
  processional: processionalScenario.nullable(),
  ringsAndBouquet: z
    .object({
      ringKeeper: short,
      ringPresenter: short,
      bouquetHolder: short,
      notes: text,
    })
    .nullable(),
  vowsAndRituals: vowsAndRitualsScenario.nullable(),
  exitAndAisle: exitAndAisleScenario.nullable(),
  congratulations: congratulationsScenario.nullable(),
})
function emptyCeremonyScenario() {
  return {
    seating: null,
    processional: null,
    ringsAndBouquet: null,
    vowsAndRituals: null,
    exitAndAisle: null,
    congratulations: null,
  }
}
const ceremonyWeatherStatuses = ['undecided', 'arranged', 'not-needed'] as const
const weatherStatus = z.enum(ceremonyWeatherStatuses)
const weatherNote = z.string().trim().max(2000)
const ceremonyPlan = z.object({
  kind: z.enum(['undecided', 'civil', 'church', 'symbolic']),
  locationMode: z.enum(['same', 'different']),
  location: short,
  venueId: z.uuid().nullish(),
  time: planningTimeSchema,
  weatherStatus,
  weatherNote,
  officiantStatus: z.enum(['searching', 'booked']),
  officiantSupplierId: z.uuid().nullish(),
  officiant: supplierPlanSchema,
  brideWitness: contact,
  groomWitness: contact,
  exitLine: z.boolean(),
  rituals: text,
  notes: text,
  scenario: ceremonyScenario,
  /** Codes of the "Stojí za to ověřit" prompts the couple hid; the planner owns the codes. */
  hiddenPromptCodes: z
    .array(z.string().trim().min(1).max(80))
    .max(30)
    .transform((codes) => [...new Set(codes)]),
})
/** Stored plans and clients from before hidden prompts omit the field. */
const ceremonyPlanInput = ceremonyPlan.extend({
  hiddenPromptCodes: ceremonyPlan.shape.hiddenPromptCodes.default([]),
})
export const weddingPlanSchema = z.object({
  venue: z.object({
    arrangement: z.enum(['undecided', 'together', 'separate']),
    booked: z.enum(['undecided', 'yes', 'no']),
    style: short,
  }),
  ceremony: ceremonyPlan,
  /** Legacy, read only: Foto a video keeps its own plan and converts this on first read. */
  photo: z.object({
    photographer: supplierPlanSchema,
    engagement: z.boolean(),
    videoStatus: z.enum(['searching', 'booked', 'unneeded']),
    video: supplierPlanSchema,
  }),
  beauty: z.object({
    makeup: supplierPlanSchema,
    hair: supplierPlanSchema,
    makeupTrial: trial,
    hairTrial: trial,
    extras: z
      .array(
        z.object({
          id: z.uuid(),
          name: short.min(1),
          service: z.enum(['makeup', 'hair']),
          amount: z.number().int().min(0).max(100_000_000),
        }),
      )
      .max(100),
  }),
  decor: z.object({
    style: short,
    colors: z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/)).max(8),
    provider: z.enum(['undecided', 'venue', 'coordinator', 'self']),
    decorations: supplierPlanSchema,
    florist: supplierPlanSchema,
    tent: supplierPlanSchema,
    stationeryProvider: z.enum(['self', 'designer']),
    designer: supplierPlanSchema,
    stationery: z.array(
      z.enum([
        'seating',
        'welcome',
        'schedule',
        'menu',
        'names',
        'invitations',
        'directions',
        'info',
      ]),
    ),
    notes: text,
  }),
  attire: z.object({
    dress: supplierPlanSchema,
    secondDress: z.boolean(),
    changeInto: short,
    shoes: short,
    garter: short,
    veil: short,
    jewelry: short,
    suit: supplierPlanSchema,
    rings: supplierPlanSchema,
  }),
  guests: z.object({
    driverBooked: z.boolean(),
    driver: supplierPlanSchema,
  }),
  schedule: z.object({
    overrides: z
      .array(
        z.object({
          id: short.min(1),
          title: short.min(1),
          time: planningTimeSchema,
          hidden: z.boolean(),
          order: z.number().int().min(0),
          category: z.enum(scheduleCategories).optional(),
          description: text.optional(),
        }),
      )
      .max(500),
  }),
})
export type WeddingPlan = z.infer<typeof weddingPlanSchema>
export type WeddingPlanSection = keyof WeddingPlan
const persistedWeddingPlanSchema = weddingPlanSchema.extend({ ceremony: z.unknown() })
const persistedCeremonyScenario = ceremonyScenario.extend({
  seating: seatingScenario
    .extend({
      usherRole: seatingScenario.shape.usherRole.optional(),
      notes: text.optional(),
      reservedSeats: text.optional(),
      wishes: text.optional(),
    })
    .nullable(),
  processional: processionalScenario
    .extend({
      entries: z
        .array(z.object({ id: z.uuid(), person: short, companion: short.optional() }))
        .max(50),
      cueByRole: processionalScenario.shape.cueByRole.optional(),
    })
    .nullable(),
  vowsAndRituals: z
    .object({
      items: z
        .array(
          ritualItem.extend({
            kind: z.enum(['undecided', ...ritualItem.shape.kind.options]),
            notes: text.optional(),
          }),
        )
        .max(50),
      notes: text.optional(),
    })
    .nullable(),
  exitAndAisle: exitAndAisleScenario
    .extend({
      decision: ceremonyDecision.optional(),
      location: short.optional(),
      supplies: short.optional(),
    })
    .nullable(),
  congratulations: congratulationsScenario
    .extend({ decision: ceremonyDecision.optional() })
    .nullable(),
})
const persistedCeremonyPlan = ceremonyPlanInput.extend({
  weatherStatus: z.string().trim().optional(),
  weatherNote: weatherNote.optional(),
  notes: text.default(''),
  scenario: persistedCeremonyScenario.optional(),
})

export function safeParseCeremonyPlan(value: unknown) {
  const parsed = persistedCeremonyPlan.safeParse(value)
  if (!parsed.success) return parsed
  const ceremony = parsed.data
  const scenario = ceremony.scenario ?? emptyCeremonyScenario()
  const exitAndAisle = scenario.exitAndAisle
    ? {
        preparedBy: scenario.exitAndAisle.preparedBy,
        notes: [
          scenario.exitAndAisle.location,
          scenario.exitAndAisle.supplies,
          scenario.exitAndAisle.notes,
        ]
          .filter(Boolean)
          .join('\n\n')
          .slice(0, 5000),
      }
    : ceremony.exitLine
      ? { preparedBy: '', notes: '' }
      : null
  return ceremonyPlan.safeParse({
    ...ceremony,
    ...persistedWeather(ceremony.weatherStatus ?? '', ceremony.weatherNote ?? ''),
    exitLine: Boolean(exitAndAisle),
    scenario: {
      ...scenario,
      seating: persistedSeating(scenario.seating),
      processional: scenario.processional && {
        ...scenario.processional,
        entries: scenario.processional.entries.map(({ id, person, companion }) => ({
          id,
          person: (companion ? `${person} s ${companion}` : person).slice(0, 300),
        })),
        cueByRole:
          scenario.processional.cueByRole ??
          (scenario.processional.cueBy ? ('someone-else' as const) : null),
      },
      vowsAndRituals: scenario.vowsAndRituals && {
        items: scenario.vowsAndRituals.items.map(({ id, kind, title, people }) => ({
          id,
          kind: kind === 'undecided' ? ('custom' as const) : kind,
          title,
          people,
        })),
        notes:
          scenario.vowsAndRituals.notes ??
          scenario.vowsAndRituals.items
            .filter((item) => item.notes)
            .map((item) => (item.title ? `${item.title}: ${item.notes}` : item.notes))
            .join('\n\n')
            .slice(0, 5000),
      },
      exitAndAisle,
    },
  })
}

function persistedSeating(
  seating: z.infer<typeof persistedCeremonyScenario>['seating'] | undefined,
) {
  if (!seating) return null
  return {
    usherRole: seating.usherRole ?? (seating.usher ? ('someone-else' as const) : null),
    usher: seating.usher,
    notes:
      seating.notes ??
      [seating.reservedSeats, seating.wishes].filter(Boolean).join('\n\n').slice(0, 5000),
  }
}

function persistedWeather(status: string, note: string) {
  const known = weatherStatus.safeParse(status)
  if (known.success) return { weatherStatus: known.data, weatherNote: note }
  return {
    weatherStatus: 'undecided' as const,
    weatherNote: [status, note].filter(Boolean).join('\n').slice(0, 2000),
  }
}

export function parseCeremonyPlan(value: unknown): WeddingPlan['ceremony'] {
  const parsed = safeParseCeremonyPlan(value)
  if (!parsed.success) throw parsed.error
  return parsed.data
}

export function parseWeddingPlan(value: unknown): WeddingPlan {
  const plan = persistedWeddingPlanSchema.parse(value)
  return weddingPlanSchema.parse({ ...plan, ceremony: parseCeremonyPlan(plan.ceremony) })
}

export const saveWeddingPlanSchema = z.discriminatedUnion('section', [
  z.object({ section: z.literal('venue'), data: weddingPlanSchema.shape.venue }),
  z.object({ section: z.literal('ceremony'), data: ceremonyPlanInput }),
  z.object({ section: z.literal('beauty'), data: weddingPlanSchema.shape.beauty }),
  z.object({ section: z.literal('decor'), data: weddingPlanSchema.shape.decor }),
  z.object({ section: z.literal('attire'), data: weddingPlanSchema.shape.attire }),
  z.object({ section: z.literal('guests'), data: weddingPlanSchema.shape.guests }),
  z.object({ section: z.literal('schedule'), data: weddingPlanSchema.shape.schedule }),
])
export type SaveWeddingPlan = z.infer<typeof saveWeddingPlanSchema>
export function emptyWeddingPlan(): WeddingPlan {
  const supplier = () => ({ name: '', contact: '' })
  return {
    venue: { arrangement: 'undecided', booked: 'undecided', style: '' },
    ceremony: {
      kind: 'undecided',
      locationMode: 'same',
      location: '',
      time: '',
      weatherStatus: 'undecided',
      weatherNote: '',
      officiantStatus: 'searching',
      officiant: supplier(),
      brideWitness: supplier(),
      groomWitness: supplier(),
      exitLine: false,
      rituals: '',
      notes: '',
      scenario: emptyCeremonyScenario(),
      hiddenPromptCodes: [],
    },
    photo: {
      photographer: supplier(),
      engagement: false,
      videoStatus: 'searching',
      video: supplier(),
    },
    beauty: {
      makeup: supplier(),
      hair: supplier(),
      makeupTrial: { date: '', amount: 0 },
      hairTrial: { date: '', amount: 0 },
      extras: [],
    },
    decor: {
      style: '',
      colors: [],
      provider: 'undecided',
      decorations: supplier(),
      florist: supplier(),
      tent: supplier(),
      stationeryProvider: 'self',
      designer: supplier(),
      stationery: [],
      notes: '',
    },
    attire: {
      dress: supplier(),
      secondDress: false,
      changeInto: '',
      shoes: '',
      garter: '',
      veil: '',
      jewelry: '',
      suit: supplier(),
      rings: supplier(),
    },
    guests: { driverBooked: false, driver: supplier() },
    schedule: { overrides: [] },
  }
}
